// Vue modules
import { Preferences } from '@capacitor/preferences';
import { isReactive, reactive } from 'vue';

import SessionManager from '../classes/session.manager.js';
import { i18nManager } from '../classes/i18n.manager.js';

// AsyncLocalStorage для изоляции SSR store per-request (только Node.js)
let asyncLocalStorage = null;

async function getAsyncLocalStorage() {
  if (!asyncLocalStorage && typeof window === 'undefined') {
    const { AsyncLocalStorage } = await import('async_hooks');
    asyncLocalStorage = new AsyncLocalStorage();
  }
  return asyncLocalStorage;
}

// ============================================================================
// GLOBAL FUNCTIONS (используют useStore() для доступа к state)
// ============================================================================

// Текст ошибки для показа. Пара к setError: getError отдаёт строку (inline в
// форме, empty state), setError показывает её в глобальной плашке.
export function getError(error) {
  let errorData;

  errorData = error;

  // Обработка ошибок из fetch API (Store class)
  if (error?.info) errorData = error.info;

  // Обработка ошибок из axios
  if (error?.response?.data) errorData = error.response.data;

  // Обработка ошибок верификации
  if ((errorData?.errorCode === 'VALIDATION_ERROR' || errorData?.errors) && errorData?.errors) {
    // Собираем все сообщения об ошибках в одну строку
    const errorMessages = [];
    for (const field in errorData.errors) {
      const fieldErrors = errorData.errors[field];
      if (Array.isArray(fieldErrors)) {
        errorMessages.push(...fieldErrors);
      } else if (typeof fieldErrors === 'string') {
        errorMessages.push(fieldErrors);
      }
    }
    return errorMessages.join(', ') || errorData.message || 'Validation error';
  }

  const errorCode = errorData?.errorCode;
  if (errorCode) {
    const i18n = i18nManager.i18nInstance?.global;
    const key = `core.errors.${errorCode}`;
    return i18n?.te(key) ? i18n.t(key) : errorCode;
  }

  return errorData?.message || error?.message || 'Unknown error';
}

// Mutations
export function setError(error) {
  const state = useStore().core.state;
  state.error.message = getError(error);

  state.error.show = true;

  if (typeof window !== 'undefined' && window.requestAnimationFrame) {
    requestAnimationFrame(() => {
      setTimeout(() => (state.error.show = false), 5000);
    });
  } else {
    setTimeout(() => (state.error.show = false), 5000);
  }

  return state.error.message;
}

export function setSnack(data) {
  const state = useStore().core.state;

  // Handle different input formats
  let type = 'notification'
  let message = ''
  let duration = 3000

  if (typeof data === 'string') {
    message = data
  } else if (data instanceof Error) {
    type = 'error'
    message = data.message
  } else if (data?.response?.data) {
    // Handle API errors
    type = 'error'
    const errorData = data.response.data
    message = errorData.errorCode || errorData.message || 'Unknown error'
  } else if (typeof data === 'object') {
    type = data.type || 'notification'
    message = data.message || ''
    duration = data.duration || 3000
  }

  // Update state
  state.snack = {
    show: true,
    type,
    message,
    duration
  }

  // Auto-hide
  if (typeof window !== 'undefined' && window.requestAnimationFrame) {
    requestAnimationFrame(() => {
      setTimeout(() => {state.snack.show = false }, duration)
    });
  } else {
    setTimeout(() => {state.snack.show = false }, duration)
  }
}

// ============================================================================
// GLOBAL STORE FACTORY
// ============================================================================

// Фабрика для создания store
export function createStore() {
  // State создается для каждого store instance (изоляция SSR)
  const state = reactive({
    loading: false,
    notFound: false,

    isOpenLocationPopup: false,
    isOpenSidebar: false,
    // Пользователь свернул сайдбар руками. Пока флаг стоит, автораскрытие по
    // route.meta.sidebarOpenOnEnter не срабатывает: смена раздела не должна
    // отменять явное решение свернуть. Снимается ручным раскрытием.
    isSidebarClosedByUser: false,

    position: null,
    search: null,

    // Слот значения темы. УПРАВЛЕНИЕ — в дизайн-системе: ds.theme.set/toggle/init
    // (@martyrs/src/design-system/utils/theme.js). Здесь только хранение — ради
    // per-request SSR-изоляции и гидрации, которые даёт механика этого стора.
    theme: {
      mode: 'system',   // 'light' | 'dark' | 'system'
    },

    navigation_bar: {
      name: null,
      actions: null,
    },

    error: {
      status: '',
      headers: '',
      data: '',
      show: false,
      name: '',
      message: '',
    },
    snack: {
      show: false,
      type: 'notification',
      message: '',
      duration: 3000
    },

    // Session state - данные сессии хранятся здесь
    session: {
      token: null,
      userId: null,
      roles: null,
      accesses: []
    },
  });

  // Actions с closure на локальный state
  const actions = {
    setLoading(status) {
      state.loading = status;
    },

    // setTheme/toggleTheme ПЕРЕЕХАЛИ в дизайн-систему:
    // import { ds } from '@martyrs/src/design-system/index.js' → ds.theme.set()/toggle()

    add(array, item) {
      const existingItemIndex = array.findIndex(i => i._id === item._id);

      if (existingItemIndex === -1) {
        array.push(item);
      } else {
        array[existingItemIndex] = item;
      }
    },

    update(array, item) {
      const existingItemIndex = array.findIndex(i => i._id === item._id);
      if (existingItemIndex === -1) {
        // If the item doesn't exist, push it to the array
        array.push(item);
      } else {
        // Update the item in the array without creating a new array
        Object.assign(array[existingItemIndex], item);
      }
    },

    delete(array, item) {
      const existingItemIndex = array.findIndex(c => c._id === item._id);

      if (existingItemIndex !== -1) {
        array.splice(existingItemIndex, 1);
      }
    },

    increment(array, item) {
      console.log(array);
      console.log(item);
      const arrayItem = array.find(i => i._id === item._id);

      if (arrayItem) {
        arrayItem.quantity++;
      }
    },

    decrement(array, item) {
      const arrayItem = array.find(i => i._id === item._id);

      const arrayItemIndex = array.indexOf(arrayItem);

      if (arrayItemIndex > -1) {
        arrayItem.quantity--;

        if (arrayItem.quantity < 1) array.splice(arrayItemIndex, 1);
      }
    },

    reset(array) {
      array = [];
    },
  };

  // SessionManager создается внутри createStore для правильной изоляции в SSR
  const sessionManager = new SessionManager(state.session);

  const store = reactive({
    modules: [],

    // Core module registered by default
    core: { state, actions, session: sessionManager },

    addStore(name, storage) {
      this[name] = storage;
      this.modules.push(name);
    },

    async getInitialState() {
      const initialState = {};
      for (const [moduleName, moduleStore] of Object.entries(this)) {
        if (moduleName !== 'modules' && moduleName !== 'addStore' && moduleName !== 'getInitialState' && moduleName !== 'setInitialState') {
          if (moduleStore.state) {
            initialState[moduleName] = JSON.parse(JSON.stringify(moduleStore.state));
          }
        }
      }
      return initialState;
    },

    async setInitialState(initialState, isHydration = false) {
      const modules = Object.entries(initialState);

      for (const [moduleName, moduleState] of modules) {
        if (this[moduleName] && this[moduleName].state) {
          // При гидратации просто заменяем state целиком для скорости
          if (isHydration) {
            Object.assign(this[moduleName].state, moduleState);
          } else {
            mergeReactive(this[moduleName].state, moduleState);
          }
        }
      }
    }
  });

  function mergeReactive(target, source) {
    // Оптимизированная версия слияния
    const keys = Object.keys(source);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const sourceValue = source[key];

      if (sourceValue !== null && typeof sourceValue === 'object' && !Array.isArray(sourceValue)) {
        if (isReactive(target[key])) {
          mergeReactive(target[key], sourceValue);
        } else {
          target[key] = sourceValue;
        }
      } else {
        target[key] = sourceValue;
      }
    }
  }

  return store;
}

// ============================================================================
// SSR AND SINGLETON
// ============================================================================

// Синглтон для клиента
let clientStore = null;

// Экспорт для использования als.run() в renderApp
export async function getSSRLocalStorage() {
  return getAsyncLocalStorage();
}

// AsyncLocalStorage для SSR (изоляция per-request)
export async function setSSRStore(store) {
  if (typeof window === 'undefined') {
    const als = await getAsyncLocalStorage();
    als?.enterWith(store);
  }
}

export async function clearSSRStore() {
  if (typeof window === 'undefined') {
    const als = await getAsyncLocalStorage();
    als?.enterWith(null);
  }
}

export function useStore() {
  if (typeof window === 'undefined') {
    // SSR: используем store из AsyncLocalStorage (может быть null если не инициализирован)
    const store = asyncLocalStorage?.getStore();
    if (store) {
      return store;
    }
    // Fallback для обратной совместимости
    console.warn('[WARN] SSR store not in AsyncLocalStorage, creating new store instance');
    return createStore();
  }
  // Client: синглтон
  if (!clientStore) {
    clientStore = createStore();
  }
  return clientStore;
}

// ============================================================================
// SESSION ACCESSOR
// ============================================================================

/**
 * Получить текущий экземпляр SessionManager
 * Использовать вместо прямого импорта session для правильной работы с SSR
 */
export function useSession() {
  const store = useStore();
  return store.core.session;
}

// ============================================================================
// EXPORTS
// ============================================================================

// Не экспортируем singleton state/actions - теперь используется useStore()
export default useStore();
